/**
 * MerchLinks — Creator storefront link-out cards.
 *
 * Renders a grid of "Shop Merch"-style cards for each storefront the parser
 * detected on the creator's profile (or an owner added manually via
 * EditDrop). Each card is a link out to the creator's existing storefront —
 * no catalog sync, no in-app checkout, no platform fee capture. The MVP
 * surface is "this creator has merch, here's where to find it."
 *
 * Storefront platforms are kept SEPARATE from social platforms (see
 * `SocialLinks.tsx`) because they're commerce surfaces with their own
 * iconography and treatment — mixing them into the social row would dilute
 * both. The DropPage MERCH tab renders this widget alone.
 */

import {
    FaShopify,
    FaBagShopping,
    FaShirt,
    FaMugHot,
    FaStore,
} from "react-icons/fa6";
import { SiStreamlabs, SiGumroad } from "react-icons/si";
import "./MerchLinks.scss";

/**
 * Storefront platform discriminator. Mirrors the StorefrontPlatformType enum
 * on DropModel. Local type because the generated `@devour/client` Drop type
 * won't carry the new `storefronts[]` field until the client is regenerated.
 */
export type StorefrontPlatform =
    | "STREAMLABS"
    | "SHOPIFY"
    | "FOURTHWALL"
    | "GUMROAD"
    | "KOFI"
    | "SPRING"
    | "OTHER";

export interface StorefrontEntry {
    type: StorefrontPlatform;
    url: string;
    username?: string;
}

interface MerchLinksProps {
    storefronts: StorefrontEntry[];
    /*
     * `grid` (default) and `row` layouts are both fully wired in
     * MerchLinks.scss. No consumer is passing `row` yet — reserved for a
     * future side-rail surface where horizontal scroll is preferable to a
     * column-flowing grid.
     */
    layout?: "grid" | "row";
}

const PLATFORM_ICONS: Record<StorefrontPlatform, React.ComponentType<{ className?: string }>> = {
    STREAMLABS: SiStreamlabs,
    SHOPIFY: FaShopify,
    FOURTHWALL: FaShirt,
    GUMROAD: SiGumroad,
    KOFI: FaMugHot,
    SPRING: FaBagShopping,
    OTHER: FaStore,
};

const PLATFORM_NAMES: Record<StorefrontPlatform, string> = {
    STREAMLABS: "Streamlabs Merch",
    SHOPIFY: "Shopify Store",
    FOURTHWALL: "Fourthwall Store",
    GUMROAD: "Gumroad",
    KOFI: "Ko-fi",
    SPRING: "Spring Store",
    OTHER: "Storefront",
};

const PLATFORM_COLORS: Record<StorefrontPlatform, string> = {
    STREAMLABS: "#80F5D2",
    SHOPIFY: "#96BF48",
    FOURTHWALL: "#FF4D2E",
    GUMROAD: "#FF90E8",
    KOFI: "#FF5E5B",
    SPRING: "#FFD400",
    OTHER: "#888888",
};

function isHttpUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

/*
 * Re-exported so DropHero can render storefront links as compact glyph-links in
 * the hero links row (the current "just a link" treatment) using the same
 * provider icon/name mapping — without duplicating the tables or pulling in the
 * full card grid. Also exported: isHttpUrl for the same safe-URL guard.
 */
export {PLATFORM_ICONS as STOREFRONT_ICONS, PLATFORM_NAMES as STOREFRONT_NAMES, isHttpUrl as isHttpStorefrontUrl};

export const MerchLinks: React.FC<MerchLinksProps> = ({ storefronts, layout = "grid" }) => {
    const safe = (storefronts || []).filter(s => isHttpUrl(s.url));
    if (safe.length === 0) {
        return null;
    }

    return (
        <div className={`merch-links merch-links--${layout}`}>
            {safe.map(sf => {
                const Icon = PLATFORM_ICONS[sf.type] || FaStore;
                const name = PLATFORM_NAMES[sf.type] || sf.type;
                const color = PLATFORM_COLORS[sf.type] || "#888888";
                return (
                    <a
                        key={`${sf.type}-${sf.url}`}
                        href={sf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="merch-links__card"
                        title={sf.username ? `${name} — ${sf.username}` : name}
                        style={{ "--platform-color": color } as React.CSSProperties}
                    >
                        <Icon className="merch-links__icon" />
                        <div className="merch-links__meta">
                            <div className="merch-links__name">{name}</div>
                            {sf.username &&
                                <div className="merch-links__handle">@{sf.username}</div>
                            }
                        </div>
                    </a>
                );
            })}
        </div>
    );
};
